import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Lightbulb, Wrench, Search, UploadCloud, X, FileText, Image as ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { employeeApi, suggestionApi, kaizenApi, submissionApi, resolveMediaUrl } from '../lib/api';
import { toast } from 'sonner';
import { useUnit } from '../contexts/UnitContext';

/**
 * One form for a suggestion and a kaizen.
 *
 * They are the same act of improvement recorded at two depths — an idea, and
 * an idea that was carried out — and they were filed through two unrelated
 * screens with nothing in common but the employee. So "what did Quality save
 * us this year" could not be answered from either.
 *
 * The form stays short on purpose. Six things, most of them one click:
 * type, who, a title, fifty words, what kind of benefit, and (only if the
 * benefit is commercial) how much. The attachment is optional and most
 * entries will not have one.
 */

export const BENEFIT_CATEGORIES = ['Commercial', 'Safety', 'Quality', 'Productivity', 'Other'];
export const MAX_WORDS = 50;

/** Words as a person counts them. Mirrors `count_words` on the server, which
 *  is what actually enforces the limit. */
export const countWords = (s) => String(s || '').trim().split(/\s+/).filter(Boolean).length;

const CATEGORY_STYLE = {
  Commercial:   { on: 'bg-[#15307F] text-white border-[#15307F]', dot: '#15307F' },
  Safety:       { on: 'bg-[#B42318] text-white border-[#B42318]', dot: '#B42318' },
  Quality:      { on: 'bg-[#15654B] text-white border-[#15654B]', dot: '#15654B' },
  Productivity: { on: 'bg-[#8A5A12] text-white border-[#8A5A12]', dot: '#8A5A12' },
  Other:        { on: 'bg-[#475467] text-white border-[#475467]', dot: '#475467' },
};

const isImage = (t) => String(t || '').startsWith('image/');


/**
 * Title + 50-word description + benefit + optional attachment.
 *
 * Extracted so the full HR-F-16 kaizen sheet can carry exactly the same block
 * the short form does. One definition means the word counter, the chips and
 * the conditional amount cannot drift apart between the two screens.
 *
 * `value` is the form state; `onChange(key, val)` writes one field.
 */
export function SubmissionFields({ value, onChange, errors = {}, showErrors = false }) {
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const words = countWords(value.description);
  const file = value.attachment;

  const takeFile = async (f) => {
    if (!f) return;
    setUploading(true);
    try {
      const r = await submissionApi.uploadAttachment(f);
      onChange('attachment', r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not attach that file');
    } finally { setUploading(false); }
  };

  const Err = ({ k }) => (showErrors && errors[k]
    ? <p data-testid={`err-${k}`} className="text-[11px] text-[#E11D2A] mt-1">{errors[k]}</p>
    : null);

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs font-semibold text-[#5A6377] mb-1.5 block">Title *</Label>
        <Input data-testid="submission-title" value={value.title || ''} maxLength={120}
               onChange={(e) => onChange('title', e.target.value)}
               placeholder="e.g. Reduce material wastage"
               className={showErrors && errors.title ? 'border-[#E11D2A]' : ''} />
        <Err k="title" />
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-1.5">
          <Label className="text-xs font-semibold text-[#5A6377]">Description *</Label>
          <span data-testid="word-counter"
                className={`text-[11px] tabular-nums ${words > MAX_WORDS ? 'text-[#E11D2A] font-semibold'
                            : words >= MAX_WORDS - 5 ? 'text-[#8A5A12]' : 'text-[#98A1B2]'}`}>
            {words} / {MAX_WORDS} words
          </span>
        </div>
        <textarea data-testid="submission-description" rows={3} value={value.description || ''}
                  onChange={(e) => onChange('description', e.target.value)}
                  placeholder="What is the improvement, in a few lines?"
                  className={`w-full border rounded-md p-2.5 text-sm ${
                    words > MAX_WORDS || (showErrors && errors.desc)
                      ? 'border-[#E11D2A]' : 'border-[#E6E9F0]'}`} />
        {words > MAX_WORDS && (
          <p data-testid="word-limit-msg" className="text-[11px] text-[#E11D2A] mt-1">
            Maximum {MAX_WORDS} words allowed.
          </p>
        )}
        <Err k="desc" />
      </div>

      <div>
        <Label className="text-xs font-semibold text-[#5A6377] mb-1.5 block">Benefit Category *</Label>
        <div className="flex flex-wrap gap-2">
          {BENEFIT_CATEGORIES.map((c) => (
            <button key={c} type="button" data-testid={`benefit-${c.toLowerCase()}`}
              onClick={() => onChange('benefit_category', c)}
              className={`px-3 py-1.5 rounded-full text-[13px] font-medium border transition-colors ${
                value.benefit_category === c ? CATEGORY_STYLE[c].on
                  : 'bg-white text-[#5A6377] border-[#E6E9F0] hover:bg-[#F7F8FB]'}`}>
              {c}
            </button>
          ))}
        </div>
        <Err k="cat" />
      </div>

      {/* Only Commercial carries a figure. An amount on a Safety entry would
          quietly inflate the savings total. */}
      {value.benefit_category === 'Commercial' && (
        <div data-testid="benefit-amount-wrap">
          <Label className="text-xs font-semibold text-[#5A6377] mb-1.5 block">
            Cost Saving / Benefit Amount *
          </Label>
          <div className="relative max-w-[240px]">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5A6377]">₹</span>
            <Input data-testid="benefit-amount" type="number" min="0" step="1"
                   value={value.benefit_amount ?? ''}
                   onChange={(e) => onChange('benefit_amount', e.target.value)}
                   placeholder="25000"
                   className={`pl-7 ${showErrors && errors.amt ? 'border-[#E11D2A]' : ''}`} />
          </div>
          <Err k="amt" />
        </div>
      )}

      <div>
        <Label className="text-xs font-semibold text-[#5A6377] mb-1.5 block">
          Supporting Document <span className="font-normal text-[#98A1B2]">(optional)</span>
        </Label>
        {file ? (
          <div data-testid="attachment-chosen"
               className="flex items-center gap-3 border border-[#E6E9F0] rounded-lg p-2.5 bg-white">
            {isImage(file.content_type)
              ? <img src={resolveMediaUrl(file.url)} alt={file.name}
                     className="w-12 h-12 rounded object-cover flex-shrink-0" />
              : <span className="w-12 h-12 rounded bg-[#F1F5FD] grid place-items-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-[#2563EB]" />
                </span>}
            <div className="min-w-0 flex-1">
              <p className="text-sm text-[#11182B] truncate">{file.name}</p>
              <a href={resolveMediaUrl(file.url)} target="_blank" rel="noreferrer"
                 className="text-[11px] text-[#2563EB] hover:underline">Open</a>
            </div>
            <button type="button" data-testid="attachment-remove"
                    onClick={() => onChange('attachment', null)} title="Remove"
                    className="p-1.5 rounded hover:bg-[#FDE7E9] text-[#E11D2A] flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <label data-testid="attachment-dropzone"
                 onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                 onDragLeave={() => setDragging(false)}
                 onDrop={(e) => { e.preventDefault(); setDragging(false); takeFile(e.dataTransfer?.files?.[0]); }}
                 className={`flex flex-col items-center justify-center gap-1 border-2 border-dashed rounded-lg
                             py-5 cursor-pointer transition-colors bg-white ${
                             dragging ? 'border-[#2563EB] bg-[#F1F5FD]'
                                      : 'border-[#E6E9F0] hover:bg-[#F7F8FB]'}`}>
            <UploadCloud className="w-5 h-5 text-[#98A1B2]" />
            <p className="text-[13px] text-[#5A6377]">
              {uploading ? 'Uploading…' : 'Drag & drop file here'}
            </p>
            {!uploading && <p className="text-[11px] text-[#98A1B2]">or <span className="text-[#2563EB]">choose a file</span></p>}
            <p className="text-[10px] text-[#C9CFDB]">Image, PDF, Excel or Word · up to 10 MB</p>
            <input type="file" className="hidden" disabled={uploading}
                   accept="image/*,.pdf,.xls,.xlsx,.csv,.doc,.docx"
                   onChange={(e) => { takeFile(e.target.files?.[0]); e.target.value = ''; }} />
          </label>
        )}
      </div>
    </div>
  );
}

export default function SubmissionForm({
  mode = 'both',          // 'both' | 'suggestion' | 'kaizen'
  employee = null,        // pre-selected; hides the picker when given
  initial = null,         // an existing record, for editing
  onSaved,
  onCancel,
}) {
  const { activeUnit } = useUnit();
  const editing = !!initial?.id;

  const [type, setType] = useState(
    initial?.entry_type || (mode === 'kaizen' ? 'Kaizen' : 'Suggestion'));
  const [emp, setEmp] = useState(employee);
  const [title, setTitle] = useState(initial?.title || '');
  const [description, setDescription] = useState(
    initial?.description || initial?.text || '');
  const [category, setCategory] = useState(initial?.benefit_category || '');
  const [amount, setAmount] = useState(
    initial?.benefit_amount != null ? String(initial.benefit_amount) : '');
  const [file, setFile] = useState(initial?.attachment || null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);

  // ---- employee picker ----------------------------------------------------
  const [roster, setRoster] = useState([]);
  const [query, setQuery] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const pickerRef = useRef(null);

  useEffect(() => {
    if (employee) return;
    employeeApi.list({ unit: activeUnit })
      .then((r) => setRoster(r.data || []))
      .catch(() => setRoster([]));
  }, [activeUnit, employee]);

  useEffect(() => {
    const away = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setListOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  // Searchable by name OR by the number on their card — HR knows people by
  // either, and typing a card number is faster than scrolling a roster.
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return roster.slice(0, 8);
    return roster.filter((e) =>
      (e.name || '').toLowerCase().includes(q)
      || String(e.card_no || '').toLowerCase().includes(q)
      || (e.id || '').toLowerCase().includes(q)).slice(0, 8);
  }, [roster, query]);

  // ---- validation ---------------------------------------------------------
  const words = countWords(description);
  const errs = {};
  if (!emp) errs.emp = 'Please select an employee.';
  if (!title.trim()) errs.title = 'Please enter a short title.';
  if (!description.trim()) errs.desc = 'Please describe the improvement.';
  else if (words > MAX_WORDS) errs.desc = `Maximum ${MAX_WORDS} words allowed.`;
  if (!category) errs.cat = 'Please select a benefit category.';
  if (category === 'Commercial') {
    if (String(amount).trim() === '') errs.amt = 'Please enter the benefit amount.';
    else if (Number.isNaN(Number(amount))) errs.amt = 'The benefit amount must be a number.';
    else if (Number(amount) < 0) errs.amt = 'The benefit amount cannot be negative.';
  }
  const errors = errs;
  const valid = Object.keys(errs).length === 0;
  const show = (k) => touched && errs[k];

  // ---- attachment ---------------------------------------------------------
  const takeFile = async (f) => {
    if (!f) return;
    setUploading(true);
    try {
      const r = await submissionApi.uploadAttachment(f);
      setFile(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not attach that file');
    } finally { setUploading(false); }
  };
  const [dragging, setDragging] = useState(false);
  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    takeFile(e.dataTransfer?.files?.[0]);
  };

  // ---- submit -------------------------------------------------------------
  const submit = async () => {
    setTouched(true);
    if (!valid) { toast.error(errs.emp || errs.title || errs.desc || errs.cat || errs.amt); return; }
    setSaving(true);
    const common = {
      title: title.trim(),
      benefit_category: category,
      benefit_amount: category === 'Commercial' ? Number(amount) : null,
      attachment: file || null,
    };
    try {
      if (type === 'Suggestion') {
        await suggestionApi.add(emp.id, { ...common, text: description.trim() });
      } else if (editing) {
        await kaizenApi.update(initial.id, {
          ...initial, ...common, description: description.trim(),
          suggested_by_id: emp.id, suggested_by: emp.name,
          department: emp.department,
        });
      } else {
        await kaizenApi.create({
          ...common, description: description.trim(),
          suggested_by_id: emp.id, suggested_by: emp.name,
          department: emp.department,
        });
      }
      toast.success(`${type} saved`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not save the ${type.toLowerCase()}`);
    } finally { setSaving(false); }
  };

  const Err = ({ k }) => (show(k)
    ? <p data-testid={`err-${k}`} className="text-[11px] text-[#E11D2A] mt-1">{errs[k]}</p>
    : null);

  return (
    <div className="space-y-4" data-testid="submission-form">
      {/* ---- type ---------------------------------------------------- */}
      {mode === 'both' && !editing && (
        <div>
          <Label className="text-xs font-semibold text-[#5A6377] mb-1.5 block">Type *</Label>
          <div className="flex gap-2">
            {[['Suggestion', Lightbulb], ['Kaizen', Wrench]].map(([t, Icon]) => (
              <button key={t} type="button" data-testid={`type-${t.toLowerCase()}`}
                onClick={() => setType(t)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  type === t ? 'bg-[#2563EB] text-white border-[#2563EB]'
                             : 'bg-white text-[#5A6377] border-[#E6E9F0] hover:bg-[#F7F8FB]'}`}>
                <Icon className="w-4 h-4" />{t}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ---- employee ------------------------------------------------ */}
      {!employee && (
        <div ref={pickerRef}>
          <Label className="text-xs font-semibold text-[#5A6377] mb-1.5 block">Employee *</Label>
          {emp ? (
            <div data-testid="employee-chosen"
                 className="flex items-start justify-between gap-3 border border-[#DBE6FB] bg-[#F1F5FD] rounded-lg px-3 py-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#11182B] truncate">{emp.name}</p>
                {/* Everything below is read from the record, never typed. */}
                <p className="text-[11px] text-[#5A6377] truncate">
                  {emp.card_no ? `ID ${emp.card_no}` : emp.id}
                  {emp.department ? ` · ${emp.department}` : ''}
                  {emp.unit ? ` · ${emp.unit}` : ''}
                </p>
              </div>
              <button type="button" data-testid="employee-clear"
                      onClick={() => { setEmp(null); setQuery(''); }}
                      className="text-[11px] text-[#2563EB] hover:underline flex-shrink-0">Change</button>
            </div>
          ) : (
            <div className="relative">
              <Search className="w-4 h-4 text-[#98A1B2] absolute left-3 top-1/2 -translate-y-1/2" />
              <Input data-testid="employee-search" value={query} autoComplete="off"
                     onChange={(e) => { setQuery(e.target.value); setListOpen(true); }}
                     onFocus={() => setListOpen(true)}
                     placeholder="Search employee by name or ID…"
                     className={`pl-9 ${show('emp') ? 'border-[#E11D2A]' : ''}`} />
              {listOpen && matches.length > 0 && (
                <ul data-testid="employee-results"
                    className="absolute z-20 mt-1 w-full max-h-60 overflow-auto bg-white border border-[#E6E9F0] rounded-lg shadow-lg">
                  {matches.map((m) => (
                    <li key={m.id}>
                      <button type="button" data-testid={`employee-opt-${m.id}`}
                        onClick={() => { setEmp(m); setListOpen(false); setQuery(''); }}
                        className="w-full text-left px-3 py-2 hover:bg-[#F1F5FD]">
                        <p className="text-sm text-[#11182B]">{m.name}</p>
                        <p className="text-[11px] text-[#98A1B2]">
                          {m.card_no ? `ID ${m.card_no}` : ''}{m.department ? ` · ${m.department}` : ''}
                          {m.unit ? ` · ${m.unit}` : ''}
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <Err k="emp" />
        </div>
      )}

      {/* The same block the kaizen sheet carries — one definition, so the
          counter, the chips and the conditional amount cannot drift apart. */}
      <SubmissionFields
        value={{ title, description, benefit_category: category, benefit_amount: amount, attachment: file }}
        onChange={(k, v) => {
          if (k === 'title') setTitle(v);
          else if (k === 'description') setDescription(v);
          else if (k === 'benefit_category') setCategory(v);
          else if (k === 'benefit_amount') setAmount(v);
          else if (k === 'attachment') setFile(v);
        }}
        errors={errors} showErrors={touched} />

      <div className="flex justify-end gap-2 pt-1">
        {onCancel && <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>}
        <Button data-testid="submission-submit" onClick={submit} disabled={saving || uploading}>
          {saving ? 'Saving…' : editing ? 'Save changes' : `Submit ${type}`}
        </Button>
      </div>
    </div>
  );
}
